const SESSION_STORAGE_KEY = 'hitl_session';
const SESSION_DURATION_MS = 8 * 60 * 60 * 1000; // 8 hours in milliseconds

export interface SessionData {
  // Authentication data
  accessToken: string;
  accessTokenAD: string;
  idToken: string;
  accessTokenExpiration: string; // ISO date string

  // User information
  userId: string;
  userName: string;
  userEmail: string;
  identityType: 'gis' | 'ad' | 'none';

  // Session metadata
  sessionCreatedAt: string; // ISO date string

  selectedQueue?: string;
}

export class SessionManager {
  /**
   * Save session data to localStorage
   */
  static saveSession(data: SessionData): void {
    try {
      const sessionData = {
        ...data,
        sessionCreatedAt: data.sessionCreatedAt || new Date().toISOString(),
      };
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));

    } catch (error) {
      console.warn('Failed to save session to localStorage:', error);
    }
  }

  /**
   * Load session data from localStorage
   * Returns null if no valid session exists
   */
  static loadSession(): SessionData | null {
    try {
      const stored = localStorage.getItem(SESSION_STORAGE_KEY);
      if (!stored) {
        return null;
      }

      const sessionData: SessionData = JSON.parse(stored);
      return sessionData;
    } catch (error) {
      console.warn('Failed to load session from localStorage:', error);
      this.clearSession();
      return null;
    }
  }

  /**
   * Check if the current session is valid
   * A session is valid if:
   * 1. It exists
   * 2. It's less than 8 hours old
   * 3. The access token hasn't expired
   */
  static isSessionValid(sessionData: SessionData | null): boolean {
    if (!sessionData) {
      console.warn('[Session] No session data provided');
      return false;
    }

    // Check session age (8 hours max)
    const sessionCreatedAt = new Date(sessionData.sessionCreatedAt);
    const sessionAge = Date.now() - sessionCreatedAt.getTime();
    const sessionAgeHours = sessionAge / (1000 * 60 * 60);

    console.log(`[Session] Session age: ${sessionAgeHours.toFixed(2)} hours (created at ${sessionCreatedAt.toISOString()})`);

    if (sessionAge > SESSION_DURATION_MS) {
      console.warn(`[Session] Session expired (${sessionAgeHours.toFixed(2)} hours old, max 8 hours)`);
      return false;
    }

    // Check token expiration
    const tokenExpiration = new Date(sessionData.accessTokenExpiration);
    const now = new Date();
    const timeUntilExpiration = tokenExpiration.getTime() - now.getTime();
    const minutesUntilExpiration = timeUntilExpiration / (1000 * 60);

    console.log(`[Session] Token expires at ${tokenExpiration.toISOString()} (in ${minutesUntilExpiration.toFixed(2)} minutes)`);

    if (tokenExpiration <= now) {
      console.warn(`[Session] Access token expired at ${tokenExpiration.toISOString()}`);
      return false;
    }

    console.log('[Session] Session is valid ✓');
    return true;
  }

  /**
   * Clear session data from localStorage
   */
  static clearSession(): void {
    try {
      console.log('[Session] Clearing session from localStorage');
      localStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear session from localStorage:', error);
    }
  }

  /**
   * Update specific fields in the existing session
   */
  static updateSession(updates: Partial<SessionData>): void {
    const currentSession = this.loadSession();
    if (!currentSession) {
      console.warn('Cannot update session: no session exists');
      return;
    }

    const updatedSession = {
      ...currentSession,
      ...updates,
    };

    this.saveSession(updatedSession);
  }

  /**
   * Get session age in milliseconds
   */
  static getSessionAge(): number | null {
    const sessionData = this.loadSession();
    if (!sessionData) {
      return null;
    }

    return Date.now() - new Date(sessionData.sessionCreatedAt).getTime();
  }

  /**
   * Get time until session expires (in milliseconds)
   */
  static getTimeUntilExpiration(): number | null {
    const sessionAge = this.getSessionAge();
    if (sessionAge === null) {
      return null;
    }

    const timeRemaining = SESSION_DURATION_MS - sessionAge;
    return timeRemaining > 0 ? timeRemaining : 0;
  }
}
